import { inject, Injectable } from '@angular/core';

import { API_BASE_URL, URL_ALLOWED_LIST } from './jwt.constant';
import { MijnJwtService } from './mijn-jwt.service';

@Injectable()
export class FetchInterceptorService {
    private readonly mijnJwtService = inject(MijnJwtService);
    private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true });
    private readonly originalFetch = window.fetch?.bind(window);

    public install(): void {
        if (!window.fetch) {
            return;
        }

        window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
            if (this.isAuthHeaderNeeded(url)) {
                const token = this.mijnJwtService.getTokenForInterceptor();
                if (token) {
                    const existingHeaders =
                        init?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : {});
                    const headers = new Headers(existingHeaders as HeadersInit);
                    headers.set('Authorization', `Bearer ${token}`);
                    const rewrittenUrl = this.rewriteUrl(url);
                    const rewrittenInput = rewrittenUrl !== url && typeof input === 'string' ? rewrittenUrl : input;
                    return this.originalFetch(rewrittenInput, { ...init, headers });
                }
            }
            return this.originalFetch(input, init);
        };
    }

    private rewriteUrl(url: string): string {
        if (!this.apiBaseUrl || this.isLocalhost() || url.startsWith('http')) {
            return url;
        }
        return `${this.apiBaseUrl.replace(/\/$/, '')}${url}`;
    }

    private isLocalhost(): boolean {
        return typeof window !== 'undefined' && window.location.hostname === 'localhost';
    }

    private isAuthHeaderNeeded(url: string): boolean {
        return URL_ALLOWED_LIST.some((pattern) => {
            const regex = new RegExp(`^${pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')}`);
            return regex.test(url);
        });
    }
}
